import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';

const H = ({ children }) => <h2 className="font-serif text-2xl md:text-3xl text-[#1a2b17] mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-[#2a3a24] leading-[1.85] mb-4">{children}</p>;

export const Privacy = () => (
  <div className="min-h-screen bg-[#faf7f1]">
    <Seo title="Privacy Policy" description="How NutrifyMe by Dietitian Prachi collects, uses and protects your personal information." path="/privacy" />
    <Navbar onBook={() => {}} />
    <article className="pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-3">Legal</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1a2b17] tracking-tight">Privacy Policy</h1>
        <p className="mt-4 text-[#7a8471] text-sm">Last updated: July 21, 2025</p>

        <H>1. Introduction</H>
        <P>NutrifyMe by Dietitian Prachi (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) values your privacy. This policy explains what we collect, why we collect it, and how you can control your data.</P>

        <H>2. What we collect</H>
        <P>When you book a consultation, subscribe to our newsletter, take our diet quiz, or place an order, we collect your name, email, phone, health goals and, when relevant, delivery address. Payment details are processed by our payment partner and are never stored on our servers.</P>

        <H>3. How we use your data</H>
        <P>Your data is used only to deliver the services you asked for — dietitian consultations, meal plans, orders, freebies and program updates. We never sell your data to third parties.</P>

        <H>4. Cookies and analytics</H>
        <P>We use minimal cookies to remember your login session and shopping cart, and privacy-friendly analytics to understand how visitors use the site. You can clear cookies anytime from your browser settings.</P>

        <H>5. Data storage and security</H>
        <P>Your data is stored securely on encrypted infrastructure. Access is restricted to authorised NutrifyMe team members bound by confidentiality agreements. We follow reasonable industry practices to protect against unauthorised access, alteration or destruction.</P>

        <H>6. Your rights</H>
        <P>You can request a copy of your data, ask us to correct it, or ask us to delete it at any time by emailing hello@nutrifyme.com. We will respond within 30 days.</P>

        <H>7. Third-party services</H>
        <P>We use trusted partners for email delivery, payments and hosting. Each partner is contractually obligated to protect your data. We share only the minimum information required for those services to work.</P>

        <H>8. Children</H>
        <P>Our services are designed for adults 18 and above. We do not knowingly collect data from anyone under 18. Parents seeking pediatric nutrition consultations must reach out on behalf of their child.</P>

        <H>9. Changes to this policy</H>
        <P>We may update this policy from time to time. Material changes will be notified by email or a prominent site notice. The &quot;Last updated&quot; date at the top of this page reflects the most recent revision.</P>

        <H>10. Contact us</H>
        <P>Questions? Write to hello@nutrifyme.com or call +91 95454 05936. We are based at A305, Panchshil Soho, near Panchshil Towers, Kharadi, Pune 411014.</P>

        <div className="mt-12 text-sm text-[#7a8471]">
          See also: <Link to="/terms" className="text-[#c9743a] hover:underline">Terms of Service</Link>
        </div>
      </div>
    </article>
    <Footer onBook={() => {}} />
  </div>
);

export const Terms = () => (
  <div className="min-h-screen bg-[#faf7f1]">
    <Seo title="Terms of Service" description="Terms and conditions governing your use of NutrifyMe by Dietitian Prachi." path="/terms" />
    <Navbar onBook={() => {}} />
    <article className="pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-3">Legal</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1a2b17] tracking-tight">Terms of Service</h1>
        <p className="mt-4 text-[#7a8471] text-sm">Last updated: July 21, 2025</p>

        <H>1. Acceptance of terms</H>
        <P>By using nutrifyme.com and any related services, you agree to these terms. If you do not agree, please stop using the site.</P>

        <H>2. Nature of our services</H>
        <P>NutrifyMe offers personalised nutrition programs, dietitian consultations, meal plans and wellness products. Our advice is nutritional and lifestyle-focused. It is not a substitute for medical diagnosis or treatment by a qualified physician.</P>

        <H>3. Consultations</H>
        <P>Consultations are booked online, delivered by qualified nutritionists, and completed as scheduled. Rescheduling is allowed up to 24 hours before the appointment. No-shows without notice are billed in full.</P>

        <H>4. Payments</H>
        <P>Payments are processed by our payment partner in secure test or live mode. All prices are in Indian Rupees unless otherwise noted. GST is applicable as per prevailing law.</P>

        <H>5. Refunds</H>
        <P>Consultation fees are refundable up to 24 hours before the scheduled call. Program fees are refundable within the first 7 days minus the cost of any deliverables already provided. Product refunds are handled per our refund policy shared at purchase.</P>

        <H>6. User conduct</H>
        <P>You agree not to misuse the site, upload unlawful content, spam, or attempt to gain unauthorised access to our systems. Violation may result in termination of your account without notice.</P>

        <H>7. Intellectual property</H>
        <P>All content — meal plans, blog articles, images and course materials — is the property of NutrifyMe or its licensors. You may not reproduce, distribute or resell it without written permission.</P>

        <H>8. Medical disclaimer</H>
        <P>Our nutritional guidance complements but never replaces medical care. Always inform your primary physician before starting any new nutrition plan, especially if you have chronic conditions, are pregnant, breastfeeding, or on prescribed medication.</P>

        <H>9. Limitation of liability</H>
        <P>To the maximum extent permitted by law, NutrifyMe is not liable for indirect, incidental, or consequential damages arising from your use of the site or our services.</P>

        <H>10. Governing law</H>
        <P>These terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts in Pune, Maharashtra.</P>

        <H>11. Contact</H>
        <P>Questions about these terms? Email hello@nutrifyme.com.</P>

        <div className="mt-12 text-sm text-[#7a8471]">
          See also: <Link to="/privacy" className="text-[#c9743a] hover:underline">Privacy Policy</Link>
        </div>
      </div>
    </article>
    <Footer onBook={() => {}} />
  </div>
);

export const Refunds = () => (
  <div className="min-h-screen bg-[#faf7f1]">
    <Seo title="Refund Policy" description="NutrifyMe refund policy for consultations, programs and products." path="/refunds" />
    <Navbar onBook={() => {}} />
    <article className="pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-3">Legal</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1a2b17] tracking-tight">Refund Policy</h1>
        <p className="mt-4 text-[#7a8471] text-sm">Last updated: July 21, 2025</p>

        <H>Consultations</H>
        <P>Free 15-minute discovery calls carry no charge and hence no refund. Paid consultations are fully refundable if cancelled at least 24 hours before the scheduled slot.</P>

        <H>Nutrition programs</H>
        <P>Full refunds are available within 7 days of purchase, minus the cost of any deliverables already provided (initial assessment, first plan, one-on-one calls). After 7 days, refunds are considered on a case-by-case basis.</P>

        <H>Wellness products</H>
        <P>Sealed and unopened products can be returned within 14 days of delivery for a full refund. Opened or partially used products are non-refundable for hygiene reasons, unless a manufacturing defect is proven.</P>

        <H>Refund timeline</H>
        <P>Approved refunds are processed within 5 to 7 business days back to the original payment method. Bank timelines may add another 3 to 5 days.</P>

        <H>How to request</H>
        <P>Email hello@nutrifyme.com from the same address used for booking, including your order or booking ID. Our team responds within 24 hours.</P>
      </div>
    </article>
    <Footer onBook={() => {}} />
  </div>
);
